import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Link from 'components/Link';
import { useTranslation } from 'hooks';
import PlacementOverview from 'components/Placements/PlacementOverview';
import PlacementContact from 'components/Placements/PlacementContact';
import { Loader } from 'components/styledComponents';

import {
  Button,
  ButtonContainer,
  Container,
  InformationContainer as BaseInformationContainer,
  LoaderContainer,
  RotatingSelect
} from './style';

// Filter out widget so it doesn't get passed to the DOM
const InformationContainer = ({ widget, ...rest }) => (
  <BaseInformationContainer {...rest} $widget={widget} />
);

function PlacementSummary({
  className = '',
  loading = false,
  placement = {},
  placements = [],
  allPlacements = [],
  showAllPlacements = false,
  widget = false,
  onSelectedPlacementChange = () => {},
  videoURLs = null,
  language = 'en',
  spinnerSize = '40'
}) {
  const { t } = useTranslation();

  const hasData = useMemo(() => placement && placement.id != null, [placement]);
  const finalPlacements = showAllPlacements ? allPlacements : placements;

  const placementsKeyValue = useMemo(
    () =>
      finalPlacements.map(p => ({
        key: p.id,
        name: p.name,
        placement: p
      })),
    [finalPlacements]
  );

  const onPlacementChange = useCallback(
    (_, item) => {
      if (onSelectedPlacementChange) onSelectedPlacementChange(item.placement);
    },
    [onSelectedPlacementChange]
  );

  return (
    <Container className={className}>
      <Loader
        className={className + ' placement-overview'}
        component={LoaderContainer}
        loading={loading}
        spinnerSize={spinnerSize}
      >
        <InformationContainer widget={widget}>
          <PlacementOverview
            personalInformationHeader={
              <RotatingSelect
                items={placementsKeyValue}
                selectedKey={placement ? placement.id : null}
                onChange={onPlacementChange}
              />
            }
            placement={placement}
          />
        </InformationContainer>

        <InformationContainer widget={widget}>
          <PlacementContact placement={placement} />
        </InformationContainer>

        <ButtonContainer>
          {widget && placement && placement.reportBillingViaPortal === '100' && (
            <Button
              color="primary"
              component={Link}
              disabled={!hasData}
              label={'+ ' + t('button.viewDetails')}
              to={'/placement/' + placement.id}
              variant="outlined"
            />
          )}

          {videoURLs && (
            <Button
              color="primary"
              variant="outlined"
              label={t('button.howTo')}
              onClick={() =>
                language === 'de' ? window.open(videoURLs.de) : window.open(videoURLs.en)
              }
            />
          )}
        </ButtonContainer>
      </Loader>
    </Container>
  );
}

PlacementSummary.propTypes = {
  className: PropTypes.string,
  loading: PropTypes.bool,
  placement: PropTypes.object,
  placements: PropTypes.array,
  allPlacements: PropTypes.array,
  showAllPlacements: PropTypes.bool,
  widget: PropTypes.bool,
  onSelectedPlacementChange: PropTypes.func,
  videoURLs: PropTypes.object,
  language: PropTypes.string,
  spinnerSize: PropTypes.string
};

export default PlacementSummary;
